import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { apiUrl } from '../config/api';
import { EmailInput } from './EmailInput';
import { validateEmail } from '../utils/emailValidation';
import { trackFormStart, trackGenerateLead } from '../utils/analytics';

interface CurrentUser {
  id: string;
  email: string;
  fullName: string;
  company?: string;
}

interface InterestFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  invitationEmail: string;
  inviteToken?: string | null;
  formType?: 'request-access' | 'team-invitation';
  onSubmitSuccess?: () => void;
  currentUser?: CurrentUser | null;
}

export function InterestFormModal({ isOpen, onClose, invitationEmail, inviteToken, formType = 'request-access', onSubmitSuccess, currentUser }: InterestFormModalProps) {
  const isTeamInvitation = formType === 'team-invitation';

  const initialFormData = useMemo(() => ({
    companyName: isTeamInvitation ? (currentUser?.company || '') : '',
    contactName: isTeamInvitation ? (currentUser?.fullName || '') : '',
    contactEmail: isTeamInvitation ? (currentUser?.email || invitationEmail || '') : (invitationEmail || ''),
    recruiterType: isTeamInvitation ? 'internal' : '',
    fieldOfWork: '',
    teamRole: '',
    teamSize: '',
    recruitmentTeamSize: '',
    teamEmails: [''],
    teamMemberNames: [''],
    message: ''
  }), [currentUser?.company, currentUser?.email, currentUser?.fullName, invitationEmail, isTeamInvitation]);

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [dataProtectionAccepted, setDataProtectionAccepted] = useState(false);
  const [showDataProtectionModal, setShowDataProtectionModal] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [emailValidation, setEmailValidation] = useState<{ [key: string]: boolean }>({});
  const formStartTracked = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setError('');
      setDataProtectionAccepted(false);
      setSubmitted(false);
    }
  }, [initialFormData, isOpen]);

  const handleAddEmail = () => {
    if (formData.teamEmails.length < 10) {
      setFormData(prev => ({
        ...prev,
        teamEmails: [...prev.teamEmails, ''],
        teamMemberNames: [...prev.teamMemberNames, '']
      }));
    }
  };

  const handleRemoveEmail = (index: number) => {
    if (formData.teamEmails.length > 1) {
      setFormData(prev => ({
        ...prev,
        teamEmails: prev.teamEmails.filter((_, i) => i !== index),
        teamMemberNames: prev.teamMemberNames.filter((_, i) => i !== index)
      }));
    }
  };
  const handleEmailChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      teamEmails: prev.teamEmails.map((email, i) => i === index ? value : email)
    }));
    // Validate email
    const validation = validateEmail(value);
    setEmailValidation(prev => ({
      ...prev,
      [`teamEmail_${index}`]: validation.isValid
    }));
  };

  const getEmailValidationState = (index: number) => {
    const email = formData.teamEmails[index];
    if (!email) return null;
    return validateEmail(email);
  };

  const handleNameChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      teamMemberNames: prev.teamMemberNames.map((name, i) => i === index ? value : name)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const normalizedCompanyName = formData.companyName.trim() || currentUser?.company || '';
      const normalizedContactName = formData.contactName.trim() || currentUser?.fullName || '';
      const normalizedContactEmail = formData.contactEmail.trim() || currentUser?.email || invitationEmail || '';

      if (!isTeamInvitation) {
        // Validate required fields for request access form
        if (!normalizedCompanyName) {
          setError('Company name is required');
          setLoading(false);
          return;
        }
        
        if (!normalizedContactName) {
          setError('Contact name is required');
          setLoading(false);
          return;
        }
        
        if (!normalizedContactEmail) {
          setError('Contact email is required');
          setLoading(false);
          return;
        }

        if (!formData.recruiterType) {
          setError('Please select whether you are an external or internal recruiter');
          setLoading(false);
          return;
        }

        if (!formData.fieldOfWork) {
          setError('Please select the field you hire for');
          setLoading(false);
          return;
        }
      } else {
        if (!normalizedContactName || !normalizedContactEmail) {
          setError('We could not detect your profile information. Please update your profile or contact support.');
          setLoading(false);
          return;
        }
      }

      if (!dataProtectionAccepted) {
        setError('Please accept the Data Protection Policy to continue');
        setLoading(false);
        return;
      }

      // Filter out empty invites
      const teamInvites = formData.teamEmails.map((email, index) => ({
        email: email.trim(),
        name: (formData.teamMemberNames[index] || '').trim()
      })).filter(invite => invite.email);

      if (isTeamInvitation) {
        if (teamInvites.length === 0) {
          setError('Please add at least one colleague to invite');
          setLoading(false);
          return;
        }

        if (teamInvites.some(invite => !invite.name)) {
          setError('Please provide a name for each colleague you invite');
          setLoading(false);
          return;
        }

        // Validate all team emails
        const invalidEmails = teamInvites.filter(invite => {
          const validation = validateEmail(invite.email);
          return !validation.isValid;
        });

        if (invalidEmails.length > 0) {
          setError(`Please fix the email addresses for ${invalidEmails.length} ${invalidEmails.length === 1 ? 'colleague' : 'colleagues'}. Check for typos and ensure emails are valid.`);
          setLoading(false);
          return;
        }
      }

      // Validate contact email
      const contactEmailValidation = validateEmail(normalizedContactEmail);
      if (!contactEmailValidation.isValid) {
        setError(contactEmailValidation.error || 'Please enter a valid email address');
        setLoading(false);
        return;
      }

      const teamEmails = teamInvites.map(invite => invite.email);
      const teamMemberNames = teamInvites.map(invite => invite.name);

      // Get auth token if user is logged in (for team-invitation form)
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token && formType === 'team-invitation') {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(apiUrl('/api/teams/interest'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...formData,
          companyName: normalizedCompanyName,
          contactName: normalizedContactName,
          contactEmail: normalizedContactEmail,
          recruiterType: isTeamInvitation ? (formData.recruiterType || 'internal') : formData.recruiterType,
          teamEmails,
          teamMemberNames,
          teamSize: formData.teamSize ? parseInt(formData.teamSize) : teamEmails.length,
          recruitmentTeamSize: formData.recruitmentTeamSize ? parseInt(formData.recruitmentTeamSize) : undefined,
          formType: formType // Pass formType to API
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Mark invitation as used if it exists
        if (inviteToken) {
          try {
            await fetch(apiUrl(`/api/invitations/mark-used/${inviteToken}`), {
              method: 'POST'
            });
          } catch (markUsedError) {
            console.error('Error marking invitation as used:', markUsedError);
            // Don't fail the form submission if marking as used fails
          }
        }
        
        // Cancel follow-up email if this is a Request Full Access form
        if (formType === 'request-access' && formData.contactEmail) {
          try {
            await fetch(apiUrl('/api/schedule-followup/cancel'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                userEmail: formData.contactEmail
              }),
            });
            console.log('📧 Follow-up email cancelled for user who filled Request Full Access form');
          } catch (error) {
            console.error('Error cancelling follow-up email:', error);
            // Don't fail the form submission if follow-up cancellation fails
          }
        }
        
        setSubmitted(true);
        trackGenerateLead(`interest_${formType}`);
        
        // Call onSubmitSuccess callback if provided
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      } else {
        setError(data.message || 'Failed to submit interest form');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (submitted) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '2rem',
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            backgroundColor: '#10B981',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            fontSize: '24px',
            color: 'white'
          }}>
            ✓
          </div>
          <h2 style={{ marginBottom: '1rem', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>Thank You!</h2>
          {formType === 'team-invitation' ? (
            <>
              <p style={{ marginBottom: '1rem', color: 'var(--gray)', fontFamily: 'var(--montserrat)' }}>
                Thank You for finding the course interesting enough to invite your colleagues, team members or a friend, perhaps. We appreciate your recommendation.
              </p>
              <p style={{ marginBottom: '2rem', color: 'var(--gray)', fontFamily: 'var(--montserrat)' }}>
                The person/team you invited will receive their logins within 24 hours.
              </p>
            </>
          ) : (
            <>
          <p style={{ marginBottom: '1rem', color: 'var(--gray)', fontFamily: 'var(--montserrat)' }}>
            We've received your interest in full access for your team. We appreciate your recommendation.
          </p>
          <p style={{ marginBottom: '2rem', color: 'var(--gray)', fontFamily: 'var(--montserrat)' }}>
            Our team will review your request and get back to you within 24 hours.
          </p>
            </>
          )}
          <button
            onClick={onClose}
            style={{
              background: 'var(--orange)',
              color: 'var(--dark)',
              border: 'none',
              padding: '0.75rem 2rem',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              fontFamily: 'var(--montserrat)'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          .interest-form input::placeholder,
          .interest-form textarea::placeholder {
            color: var(--gray);
          }
          .interest-form input:hover,
          .interest-form textarea:hover {
            border-color: #ddd;
          }
        `}
      </style>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '2rem',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ margin: 0, color: 'var(--dark)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'var(--montserrat)' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="m22 21-3-3m0 0a5.5 5.5 0 1 0-7.78-7.78 5.5 5.5 0 0 0 7.78 7.78Z"></path>
            </svg>
            {formType === 'team-invitation' ? 'Invite your team or anyone' : 'Request Full Access'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} color="var(--gray)" />
          </button>
        </div>

        <form className="interest-form" onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {!isTeamInvitation && (
              <>
                {/* Company Name */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: 'var(--dark)',
                    marginBottom: '0.5rem',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                    onFocus={(e) => {
                      if (!formStartTracked.current) {
                        formStartTracked.current = true;
                        trackFormStart(`interest_${formType}`);
                      }
                      e.target.style.outline = 'none';
                      e.target.style.borderColor = 'var(--orange)';
                      e.target.style.boxShadow = '0 0 0 4px rgba(216, 183, 37, 0.1)';
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'var(--white)',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      color: 'var(--dark)',
                      fontFamily: 'var(--raleway)',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                    placeholder="Enter your company name"
                    onBlur={(e) => {
                      e.target.style.borderColor = '#eee';
                      e.target.style.boxShadow = 'none';
                    }}
                    required
                  />
                </div>

                {/* Contact Name */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: 'var(--dark)',
                    marginBottom: '0.5rem',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={formData.contactName}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactName: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'var(--white)',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      color: 'var(--dark)',
                      fontFamily: 'var(--raleway)',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                {/* Contact Email */}
                <EmailInput
                  value={formData.contactEmail}
                  onChange={(value) => setFormData(prev => ({ ...prev, contactEmail: value }))}
                  onValidationChange={(isValid) => setEmailValidation(prev => ({ ...prev, contactEmail: isValid }))}
                  placeholder="Enter your email address"
                  required
                  label="Email Address"
                />

                {/* Recruiter Type */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: 'var(--dark)',
                    marginBottom: '0.5rem',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Are you an external or internal recruiter? *
                  </label>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontFamily: 'var(--montserrat)' }}>
                      <input
                        type="radio"
                        name="recruiterType"
                        value="external"
                        checked={formData.recruiterType === 'external'}
                        onChange={(e) => setFormData(prev => ({ ...prev, recruiterType: e.target.value }))}
                        style={{ accentColor: 'var(--orange)' }}
                      />
                      External Recruiter
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontFamily: 'var(--montserrat)' }}>
                      <input
                        type="radio"
                        name="recruiterType"
                        value="internal"
                        checked={formData.recruiterType === 'internal'}
                        onChange={(e) => setFormData(prev => ({ ...prev, recruiterType: e.target.value }))}
                        style={{ accentColor: 'var(--orange)' }}
                      />
                      Internal Recruiter
                    </label>
                  </div>
                </div>

                {/* Field of Work */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: 'var(--dark)',
                    marginBottom: '0.5rem',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    What field do you hire for? *
                  </label>
                  <select
                    value={formData.fieldOfWork}
                    onChange={(e) => setFormData(prev => ({ ...prev, fieldOfWork: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'var(--white)',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      color: 'var(--dark)',
                      fontFamily: 'var(--raleway)',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <option value="">Select hiring field</option>
                    <option value="IT">IT / Technology</option>
                    <option value="Sales">Sales</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Finance">Finance / Banking</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Retail">Retail</option>
                    <option value="Education">Education</option>
                    <option value="Multilingual">Multilingual / International</option>
                    <option value="All">All fields / General</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Team Role */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: 'var(--dark)',
                    marginBottom: '0.5rem',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Your Role
                  </label>
                  <input
                    type="text"
                    value={formData.teamRole}
                    onChange={(e) => setFormData(prev => ({ ...prev, teamRole: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'var(--white)',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      color: 'var(--dark)',
                      fontFamily: 'var(--raleway)',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                    placeholder="e.g., Senior Recruiter, TA Manager, HR Director"
                  />
                </div>

                {/* Team Size */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: 'var(--dark)',
                    marginBottom: '0.5rem',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Team Size
                  </label>
                  <select
                    value={formData.teamSize}
                    onChange={(e) => setFormData(prev => ({ ...prev, teamSize: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'var(--white)',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      color: 'var(--dark)',
                      fontFamily: 'var(--raleway)',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <option value="">Select team size</option>
                    <option value="1-5">1-5 people</option>
                    <option value="6-10">6-10 people</option>
                    <option value="11-20">11-20 people</option>
                    <option value="21-50">21-50 people</option>
                    <option value="50+">50+ people</option>
                  </select>
                </div>

                {/* Recruitment Team Size */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: 'var(--dark)',
                    marginBottom: '0.5rem',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Recruitment Team Size
                  </label>
                  <select
                    value={formData.recruitmentTeamSize}
                    onChange={(e) => setFormData(prev => ({ ...prev, recruitmentTeamSize: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'var(--white)',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      color: 'var(--dark)',
                      fontFamily: 'var(--raleway)',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <option value="">Select recruitment team size</option>
                    <option value="1">Just me</option>
                    <option value="2-3">2-3 people</option>
                    <option value="4-5">4-5 people</option>
                    <option value="6-10">6-10 people</option>
                    <option value="10+">10+ people</option>
                  </select>
                </div>
              </>
            )}

            {isTeamInvitation && (
              <>
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  padding: '1rem',
                  border: '1px solid #e5e7eb'
                }}>
                  <p style={{ margin: 0, color: 'var(--gray)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                    Invitations will be sent as <strong>{formData.contactName || currentUser?.fullName || 'your profile name'}</strong> ({formData.contactEmail || currentUser?.email || invitationEmail}).{' '}
                    {currentUser?.company && (
                      <span>We’ll reference <strong>{currentUser.company}</strong> so your colleagues can recognise the invite.</span>
                    )}
                  </p>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: 'var(--dark)',
                    marginBottom: '0.75rem',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Invite a colleague
                  </label>
                  {formData.teamEmails.map((email, index) => {
                    const emailValidation = getEmailValidationState(index);
                    return (
                      <div key={index}>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto',
                            gap: '0.5rem',
                            marginBottom: emailValidation && !emailValidation.isValid ? '0.25rem' : '0.75rem',
                            alignItems: 'center'
                          }}
                        >
                          <input
                            type="text"
                            value={formData.teamMemberNames[index] || ''}
                            onChange={(e) => handleNameChange(index, e.target.value)}
                            onFocus={() => {
                              if (index === 0 && !formStartTracked.current) {
                                formStartTracked.current = true;
                                trackFormStart(`interest_${formType}`);
                              }
                            }}
                            style={{
                              padding: '0.75rem',
                              border: '2px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              fontFamily: 'var(--montserrat)',
                              transition: 'border-color 0.2s ease',
                              minWidth: 0
                            }}
                            placeholder="Full name"
                          />
                          <div style={{ minWidth: 0, position: 'relative' }}>
                            <input
                              type="email"
                              value={email}
                              onChange={(e) => handleEmailChange(index, e.target.value)}
                              onBlur={() => {
                                // Trigger validation on blur
                                const validation = validateEmail(email);
                                setEmailValidation(prev => ({
                                  ...prev,
                                  [`teamEmail_${index}`]: validation.isValid
                                }));
                              }}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                paddingRight: email && emailValidation ? '2.5rem' : '0.75rem',
                                border: `2px solid ${
                                  emailValidation?.isValid === false 
                                    ? '#dc2626' 
                                    : emailValidation?.isValid === true 
                                      ? '#10b981' 
                                      : '#e5e7eb'
                                }`,
                                borderRadius: '8px',
                                fontSize: '1rem',
                                fontFamily: 'var(--montserrat)',
                                transition: 'border-color 0.2s ease',
                                minWidth: 0
                              }}
                              placeholder="email@company.com"
                            />
                            {email && emailValidation && (
                              <div
                                style={{
                                  position: 'absolute',
                                  right: '0.75rem',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  pointerEvents: 'none'
                                }}
                              >
                                {emailValidation.isValid ? (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#10b981"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M20 6L9 17l-5-5" />
                                  </svg>
                                ) : (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#dc2626"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="12" y1="8" x2="12" y2="12" />
                                    <line x1="12" y1="16" x2="12.01" y2="16" />
                                  </svg>
                                )}
                              </div>
                            )}
                          </div>
                          {formData.teamEmails.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveEmail(index)}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '0.75rem 1rem',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                fontFamily: 'var(--montserrat)'
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        {email && emailValidation && !emailValidation.isValid && (
                          <div
                            style={{
                              marginBottom: '0.75rem',
                              color: '#dc2626',
                              fontSize: '0.75rem',
                              fontFamily: 'var(--montserrat)',
                              paddingLeft: '0.25rem'
                            }}
                          >
                            {emailValidation.error}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {formData.teamEmails.length < 10 && (
                    <button
                      type="button"
                      onClick={handleAddEmail}
                      style={{
                        background: 'var(--orange)',
                        color: 'var(--dark)',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0.5rem 1rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        fontFamily: 'var(--montserrat)'
                      }}
                    >
                      + Invite Another Colleague
                    </button>
                  )}
                </div>
              </>
            )}

            {/* Message */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'var(--dark)',
                marginBottom: '0.5rem',
                fontFamily: 'var(--montserrat)'
              }}>
                Additional Message
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                rows={4}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'var(--white)',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  color: 'var(--dark)',
                  fontFamily: 'var(--raleway)',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  resize: 'vertical'
                }}
                placeholder={isTeamInvitation ? 'Add a note for our team (optional)' : 'Tell us about your recruitment challenges or specific needs...'}
              />
            </div>

            {/* Data Protection Checkbox */}
            <div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer', fontFamily: 'var(--montserrat)', fontSize: '0.875rem' }}>
                <input
                  type="checkbox"
                  checked={dataProtectionAccepted}
                  onChange={(e) => setDataProtectionAccepted(e.target.checked)}
                  style={{ marginTop: '2px', accentColor: 'var(--orange)' }}
                  required
                />
                <span>
                  I have read and accept the{' '}
                  <button
                    type="button"
                    onClick={() => setShowDataProtectionModal(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--orange)',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontFamily: 'var(--montserrat)',
                      fontSize: '0.875rem'
                    }}
                  >
                    Data Protection Policy
                  </button>
                  {' '}*
                </span>
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '1rem',
                color: '#dc2626',
                fontSize: '0.875rem',
                fontFamily: 'var(--montserrat)'
              }}>
                {error}
              </div>
            )}

            {/* Submit Button */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  background: 'white',
                  color: 'var(--gray)',
                  border: '2px solid #e5e7eb',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontFamily: 'var(--montserrat)',
                  transition: 'all 0.2s ease'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  background: loading ? '#9ca3af' : 'var(--orange)',
                  color: 'var(--dark)',
                  border: 'none',
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--montserrat)',
                  transition: 'all 0.2s ease'
                }}
              >
                {loading ? (isTeamInvitation ? 'Sending...' : 'Submitting...') : (isTeamInvitation ? 'Send Invitations' : 'Submit Request')}
              </button>
            </div>
          </div>
        </form>
      </div>
      
      {/* Data Protection Modal */}
      {showDataProtectionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{ margin: 0, color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
                Data Protection Policy
              </h2>
              <button
                onClick={() => setShowDataProtectionModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--gray)'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ lineHeight: '1.8', fontSize: '1rem', fontFamily: 'var(--montserrat)' }}>
              <p style={{ marginBottom: '1rem', fontSize: '0.9rem', color: 'var(--gray)', fontWeight: '600' }}>
                <strong>Recberry s.r.o. Data Protection Policy</strong><br/>
                <strong>Version (Effective): October 24, 2025</strong>
              </p>
              <p style={{ marginBottom: '1rem' }}>
                Your privacy and data protection are our top priorities at Recberry s.r.o. This Data Protection Policy applies to all data collections and processing activities conducted by Recberry s.r.o., particularly through our website, email communications, and marketing services. For purposes of this Policy, any reference to "Recberry," "we," "us," or "our" means Recberry s.r.o. and its affiliates. This Policy describes how we collect, use, share, and otherwise process your personal data in compliance with the General Data Protection Regulation (GDPR) (EU Regulation 2016/679). It also outlines your choices regarding the use, access, correction, and deletion of your personal data. The use of personal data is limited to the purposes outlined herein, such as providing marketing services, sending newsletters, and contacting you as requested.
              </p>
              <p style={{ marginBottom: '1rem' }}>
                We assure you that your personal data is used solely within our company to promote our products and services through activities like newsletters and direct communications. We do not share, sell, or transfer your data to any external parties for their own purposes beyond the necessary service providers outlined in this Policy. You can unsubscribe from our communications or request data deletion at any time, and we will promptly honor such requests.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>1. Data Controller</h3>
              <p style={{ marginBottom: '1rem' }}>
                <strong>Recberry s.r.o.</strong><br/>
                IČ: 04972236 | DIČ: CZ04972236<br/>
                Tupolevova 741, 199 00 Prague 9 - Letňany, Czech Republic<br/>
                Incorporated in the Commercial Register kept by the Municipal Court in Prague, Section C, File No. 256441<br/>
                <strong>Data Protection Contact:</strong> Barbora Jensik<br/>
                <strong>Email:</strong> barbora@recberry.com
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>2. Legal Basis for Processing</h3>
              <p style={{ marginBottom: '1rem' }}>
                We process your personal data based on your explicit consent under Article 6(1)(a) of the GDPR. This consent is freely given, specific, informed, and unambiguous. You may withdraw your consent at any time without affecting the lawfulness of processing based on consent before its withdrawal. No automated individual decision-making, including profiling under Article 22 GDPR, occurs in our processing activities.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>3. Collected Personal Data and How We Use It</h3>
              <p style={{ marginBottom: '1rem' }}>
                We may collect and/or process the following categories of personal data from individuals who interact with our services, such as newsletter subscribers, website visitors, or marketing contacts:
              </p>
              
              <div style={{ 
                background: '#f8f9fa', 
                padding: '1rem', 
                borderRadius: '8px', 
                marginBottom: '1rem',
                overflowX: 'auto'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600' }}>Type of Data</th>
                      <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600' }}>Description</th>
                      <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600' }}>Duration</th>
                      <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600' }}>Entity</th>
                      <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600' }}>Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.4rem', fontWeight: '500' }}>Identifiers</td>
                      <td style={{ padding: '0.4rem' }}>Names, email addresses, phone numbers, job titles, employers, contact details, IP addresses</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 7</td>
                      <td style={{ padding: '0.4rem' }}>Recberry s.r.o.</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 3.2.1-6</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.4rem', fontWeight: '500' }}>IT Information</td>
                      <td style={{ padding: '0.4rem' }}>IP addresses, device information, browser type, location data, cookies</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 7</td>
                      <td style={{ padding: '0.4rem' }}>Recberry s.r.o.</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 3.2.1-6</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.4rem', fontWeight: '500' }}>Special Categories</td>
                      <td style={{ padding: '0.4rem' }}>Sensitive data under Art. 9 GDPR, only if explicitly provided and necessary</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 7</td>
                      <td style={{ padding: '0.4rem' }}>Recberry s.r.o.</td>
                      <td style={{ padding: '0.4rem' }}>See Sec. 3.2.1-6</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p style={{ marginBottom: '1rem' }}>
                Personal data is collected and used for Recberry's legitimate business purposes as the data controller, including fulfilling contractual obligations and complying with legal requirements. We process data only for the purposes described in this Policy or for which we have obtained your consent. Specifically, we use personal data for:
              </p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.1.</strong> Providing marketing services, such as contacting you about our products or events.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.2.</strong> Sending newsletters and promotional materials related to our offerings.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.3.</strong> Facilitating communications and responding to inquiries.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.4.</strong> Analyzing trends to improve our services (e.g., website usage statistics).</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.5.</strong> For data security and compliance purposes, including internal audits.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>3.2.6.</strong> For statistical and analytical purposes, including product enhancement, but only in aggregated or anonymized forms.</li>
              </ul>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>4. How We Use Cookies and Similar Technologies</h3>
              <p style={{ marginBottom: '1rem' }}>
                Cookies and similar technologies (e.g., pixels, web beacons) are used by Recberry and our partners to analyze trends, administer the website, track user movements, and gather demographic information. We categorize cookies as follows:
              </p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>Essential cookies:</strong> Necessary for site functionality (e.g., session management).</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Analytical cookies:</strong> For performance insights (e.g., Google Analytics, anonymized data).</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Marketing cookies:</strong> For personalized content delivery.</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                We obtain your consent for non-essential cookies via a consent banner on our website, in line with GDPR and ePrivacy Directive requirements. You can manage or withdraw consent at any time through browser settings or by contacting us.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>5. Access to Personal Data</h3>
              <p style={{ marginBottom: '1rem' }}>
                Upon request and within one month (extendable to two months for complex requests, as per Art. 12 GDPR), we will provide information about your personal data we hold, including confirmation of processing, categories of data, purposes, recipients, and storage period. You can request correction, amendment, deletion, or a copy of your data in a structured, commonly used format. Contact barbora@recberry.com for these requests.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>6. Data Recipients and Transfers</h3>
              <p style={{ marginBottom: '1rem' }}>
                Recipients of your personal data are limited to sub-processors providing marketing services (e.g., email platforms) under strict GDPR-compliant data processing agreements. We have no intention to transfer data to non-EU countries or international organizations without adequate safeguards, such as Standard Contractual Clauses approved by the European Commission. All transfers, if any, are documented and ensure an equivalent level of protection. We do not share data with any other third parties for their independent use.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>7. Data Retention</h3>
              <p style={{ marginBottom: '1rem' }}>
                Personal data is retained for up to 5 years from the date of collection or until you withdraw your consent, whichever occurs first. We retain data only as long as necessary for the purposes for which it was collected, to comply with legal obligations (e.g., tax or accounting requirements), resolve disputes, or enforce agreements. Upon fulfillment of these purposes, we securely delete or anonymize the data.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>8. Data Security</h3>
              <p style={{ marginBottom: '1rem' }}>
                We employ reasonable and appropriate security measures to protect against loss, misuse, unauthorized access, disclosure, alteration, or destruction of personal data. These include:
              </p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>Technical measures:</strong> Encryption of data in transit and at rest, firewalls, secure servers.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Organizational measures:</strong> Access controls (role-based), employee training on GDPR compliance, confidentiality agreements.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Physical measures:</strong> Secure facilities for any physical records.</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                You are responsible for keeping any credentials secure. No method of transmission or storage is 100% secure; however, we regularly review and update our measures to align with industry standards.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>9. Links to Third-Party Sites</h3>
              <p style={{ marginBottom: '1rem' }}>
                Our website may contain links to third-party sites whose privacy practices differ from ours. If you submit personal data to these sites, it is governed by their policies, and we are not responsible for their actions. We encourage you to review third-party privacy policies before interacting.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>10. No Services for Minors</h3>
              <p style={{ marginBottom: '1rem' }}>
                Our services are not directed to individuals under 16 years of age. We do not knowingly collect personal data from minors. If we become aware of such collection, we will take steps to delete the data promptly.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>11. Your Rights Under GDPR</h3>
              <p style={{ marginBottom: '1rem' }}>
                In accordance with GDPR, you have the following rights regarding your personal data:
              </p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Access (Art. 15):</strong> Obtain confirmation of processing and a copy of your data.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Rectification (Art. 16):</strong> Correct inaccurate or incomplete data.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Erasure ("Right to be Forgotten," Art. 17):</strong> Request deletion when data is no longer necessary or consent is withdrawn.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Restriction of Processing (Art. 18):</strong> Limit processing under certain conditions.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Object (Art. 21):</strong> Object to processing for direct marketing or based on legitimate interests.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Data Portability (Art. 20):</strong> Receive your data in a structured, machine-readable format.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right to Withdraw Consent (Art. 7):</strong> Revoke consent at any time, without affecting prior lawfulness.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Right Not to Be Subject to Automated Decisions (Art. 22):</strong> Not applicable, as we do not engage in such processing.</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                To exercise these rights, contact barbora@recberry.com. We will respond within one month. You also have the right to lodge a complaint with a supervisory authority, such as the Czech Office for Personal Data Protection (Úřad pro ochranu osobních údajů, www.uoou.cz).
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>12. GDPR Compliance Checklist</h3>
              <p style={{ marginBottom: '1rem' }}>
                To demonstrate our commitment to GDPR compliance, we maintain the following practices:
              </p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>Lawfulness, Fairness, and Transparency:</strong> All processing is based on consent and clearly explained in this Policy.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Purpose Limitation:</strong> Data is used only for specified marketing purposes related to our products.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Data Minimization:</strong> We collect only necessary data (e.g., name and email for newsletters).</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Accuracy:</strong> We update data upon request and verify sources.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Storage Limitation:</strong> Retention is limited to 5 years or less, with regular reviews.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Integrity and Confidentiality:</strong> Security measures protect against breaches; incidents are reported within 72 hours to authorities if required (Art. 33).</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>Accountability:</strong> We maintain records of processing activities (Art. 30), conduct data protection impact assessments for high-risk activities (Art. 35), and appoint a data protection contact (Barbora Jensik).</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                We conduct annual internal audits to ensure ongoing compliance.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>13. Changes to This Policy</h3>
              <p style={{ marginBottom: '1rem' }}>
                We may update this Policy to reflect changes in our practices or legal requirements. Material changes will be notified via email or a prominent notice on our website at least 30 days before they take effect. We encourage you to review this Policy periodically.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>14. Data Transfers Outside the EEA</h3>
              <p style={{ marginBottom: '1rem' }}>
                Personal data is processed within the EEA. If any transfers outside the EEA are necessary (e.g., for sub-processors), we use Standard Contractual Clauses or other approved mechanisms to ensure adequate protection, as required by Chapter V of the GDPR.
              </p>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>15. Compliance and Supervisory Authority</h3>
              <p style={{ marginBottom: '1rem' }}>
                We comply fully with GDPR and are registered with the relevant authorities. You have the right to lodge a complaint with the Czech Office for Personal Data Protection (www.uoou.cz) if you believe your rights have been infringed.
              </p>
              
              <div style={{ 
                background: 'var(--light-gray)', 
                padding: '1.5rem', 
                borderRadius: '8px',
                marginTop: '1.5rem'
              }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>16. Data Protection Contact</h3>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>
                  For any questions, concerns, or to exercise your rights, contact:<br/>
                  <strong>Barbora Jensik</strong><br/>
                  <strong>Email:</strong> barbora@recberry.com<br/>
                  <strong>Address:</strong> Recberry s.r.o., Tupolevova 741, 199 00 Prague 9 - Letňany, Czech Republic
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
